import { useState, useRef, useEffect, useCallback, createElement } from 'react'
import {
  Eye,
  GraduationCap,
  Film,
  Users,
  ChevronDown,
  ArrowLeft,
  Radio
} from 'lucide-react'
import {
  PREVIEW_SCENARIOS,
  PREVIEW_HORIZONS_OPTIONS,
  getPreviewScenarioDisplay,
  getPreviewHorizonsDisplay,
  getPreviewClassDisplay
} from '../utils/previewScenarios'
import './PreviewBanner.css'

function PreviewBannerChip({
  chipId,
  icon,
  menuTitle,
  display,
  isOpen,
  onToggle,
  children,
  chipRef
}) {
  return (
    <div className="preview-banner-chip-wrap" ref={chipRef}>
      <button
        type="button"
        className={`preview-banner-chip${isOpen ? ' preview-banner-chip-open' : ''}`}
        onClick={() => onToggle(chipId)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        title={display.label}
      >
        {createElement(icon, {
          className: 'preview-banner-chip-icon',
          size: 14,
          strokeWidth: 2,
          'aria-hidden': true
        })}
        <span className="preview-banner-chip-label">{display.shortLabel}</span>
        <ChevronDown className="preview-banner-chip-chevron" size={12} strokeWidth={2} aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="preview-banner-menu" role="listbox" aria-label={menuTitle}>
          <div className="preview-banner-menu-header">{menuTitle}</div>
          {children}
        </div>
      )}
    </div>
  )
}

function PreviewBannerMenuOption({ selected, onSelect, label, description }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={`preview-banner-menu-option${selected ? ' preview-banner-menu-option-selected' : ''}`}
      onClick={onSelect}
    >
      <span className="preview-banner-menu-option-label">{label}</span>
      {description && (
        <span className="preview-banner-menu-option-desc">{description}</span>
      )}
    </button>
  )
}

export default function PreviewBanner({
  previewScenario,
  previewHorizonsOverride,
  previewClassId,
  previewTeacherClasses = [],
  scenarioClassLabel,
  activeLiveSession,
  showLiveButton,
  onScenarioChange,
  onHorizonsChange,
  onClassChange,
  onJoinLive,
  onBack
}) {
  const [openMenu, setOpenMenu] = useState(null)
  const bannerRef = useRef(null)
  const scenarioRef = useRef(null)
  const horizonsRef = useRef(null)
  const classRef = useRef(null)

  const toggleMenu = useCallback((chipId) => {
    setOpenMenu((prev) => (prev === chipId ? null : chipId))
  }, [])

  const closeMenu = useCallback(() => setOpenMenu(null), [])

  useEffect(() => {
    const banner = bannerRef.current
    const root = banner?.closest('.student-dashboard--preview')
    if (!banner || !root) return undefined

    const syncHeight = () => {
      root.style.setProperty('--preview-banner-height', `${banner.offsetHeight}px`)
    }

    syncHeight()
    const observer = new ResizeObserver(syncHeight)
    observer.observe(banner)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!openMenu) return undefined

    const handlePointerDown = (e) => {
      if (bannerRef.current?.contains(e.target)) return
      closeMenu()
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [openMenu, closeMenu])

  const scenarioDisplay = getPreviewScenarioDisplay(previewScenario)
  const horizonsDisplay = getPreviewHorizonsDisplay(previewHorizonsOverride)
  const classDisplay = getPreviewClassDisplay(previewClassId, previewTeacherClasses)

  return (
    <div className="preview-banner" role="status" aria-label="Aperçu élève" ref={bannerRef}>
      <div className="preview-banner-brand">
        <Eye className="preview-banner-eye" size={16} strokeWidth={2} aria-hidden="true" />
        <span className="preview-banner-brand-text">Aperçu élève</span>
      </div>

      <div className="preview-banner-controls">
        <PreviewBannerChip
          chipId="scenario"
          icon={GraduationCap}
          menuTitle="Scénario"
          display={scenarioDisplay}
          isOpen={openMenu === 'scenario'}
          onToggle={toggleMenu}
          chipRef={scenarioRef}
        >
          <PreviewBannerMenuOption
            selected={!previewScenario}
            label="Réel (données prof)"
            description="Progression et XP du professeur"
            onSelect={() => {
              onScenarioChange('')
              closeMenu()
            }}
          />
          {PREVIEW_SCENARIOS.map((s) => (
            <PreviewBannerMenuOption
              key={s.id}
              selected={previewScenario === s.id}
              label={s.label}
              description={s.description}
              onSelect={() => {
                onScenarioChange(s.id)
                closeMenu()
              }}
            />
          ))}
          {scenarioClassLabel && (
            <div className="preview-banner-menu-footer">
              Classe simulée : {scenarioClassLabel}
            </div>
          )}
        </PreviewBannerChip>

        <PreviewBannerChip
          chipId="horizons"
          icon={Film}
          menuTitle="Horizons"
          display={horizonsDisplay}
          isOpen={openMenu === 'horizons'}
          onToggle={toggleMenu}
          chipRef={horizonsRef}
        >
          {PREVIEW_HORIZONS_OPTIONS.map((opt) => (
            <PreviewBannerMenuOption
              key={opt.value || 'none'}
              selected={previewHorizonsOverride === opt.value}
              label={opt.label}
              onSelect={() => {
                onHorizonsChange(opt.value)
                closeMenu()
              }}
            />
          ))}
        </PreviewBannerChip>

        <PreviewBannerChip
          chipId="class"
          icon={Users}
          menuTitle="Classe"
          display={classDisplay}
          isOpen={openMenu === 'class'}
          onToggle={toggleMenu}
          chipRef={classRef}
        >
          <PreviewBannerMenuOption
            selected={!previewClassId}
            label="Aucune"
            description="Pas de devoirs de classe en aperçu"
            onSelect={() => {
              onClassChange('')
              closeMenu()
            }}
          />
          {previewTeacherClasses.map((tc) => (
            <PreviewBannerMenuOption
              key={tc.id}
              selected={previewClassId === tc.id}
              label={tc.name || tc.code || tc.id}
              onSelect={() => {
                onClassChange(tc.id)
                closeMenu()
              }}
            />
          ))}
        </PreviewBannerChip>

        {activeLiveSession && showLiveButton && (
          <button
            type="button"
            className="preview-banner-live-btn"
            onClick={onJoinLive}
            title="Rejoindre la session live de la classe sélectionnée"
          >
            <Radio size={14} strokeWidth={2} aria-hidden="true" />
            <span className="preview-banner-live-label">Live</span>
          </button>
        )}
      </div>

      <button
        type="button"
        className="preview-banner-back-btn"
        onClick={onBack}
        title="Retour au dashboard professeur"
      >
        <ArrowLeft className="preview-banner-back-icon" size={14} strokeWidth={2} aria-hidden="true" />
        <span className="preview-banner-back-label">Retour</span>
      </button>
    </div>
  )
}
